import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const SelectedValueBadge = ({ label, removeLabel, onRemove }: SelectedValueBadgeProps) => (
    // Capped at the field's width with the label truncated, so a long label can't spill out of the chip
    // (the full label is still in the dropdown list)
    <span className="flex max-w-full min-w-0 items-center gap-1 h-5 pl-2 pr-1 rounded-lg bg-bg-fill-transparent-secondary text-text-secondary text-body-sm">
        <span className="min-w-0 truncate">{label}</span>
        <button
            type="button"
            aria-label={removeLabel}
            onClick={onRemove}
            className="pointer-events-auto flex h-5 w-5 shrink-0 items-center justify-center cursor-pointer"
        >
            <s-icon type="x" size="small" color="subdued" />
        </button>
    </span>
);

interface SelectedValueBadgeProps {
    label: string;
    removeLabel: string;
    onRemove(): void;
}

export const SelectMultiChoicelist = <T,>(props: SelectMultiChoicelistProps<T>) => {
    const { t } = useTranslation();
    const id = useId();
    const anchorRef = useRef<HTMLDivElement>(null);
    const [anchorWidth, setAnchorWidth] = useState(0);

    useEffect(() => {
        const element = anchorRef.current;
        if (!element) return;

        const observer = new ResizeObserver(([entry]) => setAnchorWidth(entry.contentRect.width));
        observer.observe(element);
        return () => observer.disconnect();
    }, []);

    const { hasMore, isLoadingMore, hasPageError, onLoadMore } = props;
    const scrollRef = useRef<HTMLDivElement>(null);
    const sentinelRef = useRef<HTMLDivElement>(null);
    const loadedCount = props.options.length;

    useEffect(() => {
        const sentinel = sentinelRef.current;
        const root = scrollRef.current;
        if (!sentinel || !root || !onLoadMore || !hasMore || isLoadingMore || hasPageError) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) onLoadMore();
            },
            { root, rootMargin: '80px' }
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasMore, isLoadingMore, hasPageError, onLoadMore, loadedCount]);

    const onToggle = (value: T) => {
        props.onChange(
            props.values.find((v) => v === value) ? props.values.filter((v) => v !== value) : props.values.concat(value)
        );
    };

    const isSelected = (value: T) => props.values.some((v) => v === value);

    const labelOf = (value: T): string => {
        const resolved = props.selectedOptions?.find((option) => option.value === value);
        if (resolved) return resolved.label;

        const loaded = props.options.find((option) => option.value === value);
        if (loaded) return loaded.label;

        return props.renderUnresolvedLabel?.(value) ?? String(value);
    };

    const selectedRows = props.values.map((value) => ({ value, label: labelOf(value) }));

    return (
        <div className="border border-input-border relative rounded-md overflow-hidden" ref={anchorRef}>
            <div className="absolute inset-0">
                <s-clickable commandFor={id} inlineSize="100%" blockSize="100%" onClick={props.onOpen} />
            </div>
            <div className="absolute right-2 top-0 bottom-0 flex items-center pointer-events-none">
                <s-icon type="select" size="small" color="subdued" />
            </div>
            <div className="relative pl-2 pr-7 py-1.5 flex flex-wrap items-center gap-2 size-full min-h-8.5 pointer-events-none">
                {props.isResolvingSelection ? (
                    props.values.map((_, i) => (
                        <div key={i} className="h-5 w-24 animate-pulse rounded-lg bg-gray-200" />
                    ))
                ) : selectedRows.length ? (
                    selectedRows.map((row, i) => (
                        <SelectedValueBadge
                            key={i}
                            label={row.label}
                            removeLabel={t('MULTI_SELECT.REMOVE', { label: row.label })}
                            onRemove={() => onToggle(row.value)}
                        />
                    ))
                ) : (
                    <s-text color="subdued">{props.placeholder}</s-text>
                )}
            </div>
            <s-popover id={id} inlineSize={anchorWidth ? `${anchorWidth}px` : 'auto'}>
                <div ref={scrollRef} className={`overflow-y-auto ${props.maxHeightClassName ?? 'max-h-64'}`}>
                    <div className="w-full p-2">
                        {props.options.map((option, i) => (
                            <div
                                key={i}
                                className={`flex gap-2 w-full items-center py-1 px-1 ${
                                    option.disabled
                                        ? 'text-text-disabled cursor-not-allowed'
                                        : 'hover-bg cursor-pointer'
                                }`}
                                aria-disabled={option.disabled || undefined}
                                onClick={() => {
                                    if (option.disabled) return;
                                    onToggle(option.value);
                                }}
                            >
                                <s-checkbox
                                    checked={isSelected(option.value)}
                                    disabled={option.disabled || undefined}
                                />
                                {option.label}
                            </div>
                        ))}

                        <div ref={sentinelRef} className="h-px" />
                        {props.isLoadingMore && (
                            <div className="flex justify-center py-2">
                                <s-spinner accessibilityLabel={t('MULTI_SELECT.LOADING')} size="base" />
                            </div>
                        )}
                        {props.hasPageError && !props.isLoadingMore && (
                            <div className="flex flex-col gap-1 py-2">
                                <s-text color="subdued">{t('MULTI_SELECT.LOAD_ERROR')}</s-text>
                                <s-clickable onClick={props.onRetry}>
                                    <s-text>{t('MULTI_SELECT.RETRY')}</s-text>
                                </s-clickable>
                            </div>
                        )}
                    </div>
                </div>
            </s-popover>
        </div>
    );
};

interface SelectMultiChoicelistProps<T> {
    options: SelectMultiChoicelistOption<T>[];
    values: T[];
    onChange(values: T[]): void;
    placeholder?: string;
    selectedOptions?: SelectMultiChoicelistOption<T>[];
    isResolvingSelection?: boolean;
    hasMore?: boolean;
    isLoadingMore?: boolean;
    hasPageError?: boolean;
    maxHeightClassName?: string;
    onLoadMore?(): void;
    onRetry?(): void;
    onOpen?(): void;
    renderUnresolvedLabel?(value: T): string;
}

export interface SelectMultiChoicelistOption<T> {
    value: T;
    label: string;
    disabled?: boolean;
}
